import { useState, useEffect, useCallback, useRef } from "react";
import { useCallNotifications } from "./useCallNotifications";
import { 
  CallManagementService, 
  ActiveCall, 
  CallParticipant,
  InitiateCallRequest,
  JoinCallRequest,
  CallType
} from "../services/callManagementService";
import { trackActivity } from "../redux/Actions/ProfessionalMonitoringActions";
import { store } from "../redux/store";

interface UseCallManagerProps {
  userId: string | null;
  chatId?: string;
  onCallInitiated?: (call: ActiveCall) => void;
  onCallJoined?: (call: ActiveCall) => void;
  onCallLeft?: (callId: string) => void;
  onCallEnded?: (callId: string) => void;
  onError?: (error: string) => void;
}

export const useCallManager = ({
  userId,
  chatId,
  onCallInitiated,
  onCallJoined,
  onCallLeft,
  onCallEnded,
  onError
}: UseCallManagerProps) => {
  // State
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);
  const [currentCall, setCurrentCall] = useState<ActiveCall | null>(null);
  const [isInCall, setIsInCall] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [callParticipants, setCallParticipants] = useState<CallParticipant[]>([]);

  // Refs for stable callbacks
  const callbacksRef = useRef({
    onCallInitiated,
    onCallJoined,
    onCallLeft,
    onCallEnded,
    onError
  });

  // Update callbacks ref
  useEffect(() => {
    callbacksRef.current = {
      onCallInitiated,
      onCallJoined,
      onCallLeft,
      onCallEnded,
      onError
    };
  }, [onCallInitiated, onCallJoined, onCallLeft, onCallEnded, onError]);

  // WebSocket notifications
  const { isConnected } = useCallNotifications(userId, {
    onCallInitiated: useCallback((data) => {
      console.log("📞 Call initiated notification:", data);
      
      // Add to active calls if not already present
      setActiveCalls(prev => {
        const exists = prev.some(call => call.id === data.callId);
        if (!exists) {
          return [...prev, data.callData];
        }
        return prev;
      });

      callbacksRef.current.onCallInitiated?.(data.callData);
    }, []),

    onUserJoinedCall: useCallback((data) => {
      console.log("👥 User joined call:", data);
      
      // Refresh call participants if it's the current call
      if (currentCall?.id === data.callId) {
        refreshCallParticipants(data.callId);
      }
    }, [currentCall?.id]),

    onUserLeftCall: useCallback((data) => {
      console.log("👋 User left call:", data);
      
      // Refresh call participants if it's the current call
      if (currentCall?.id === data.callId) {
        refreshCallParticipants(data.callId);
      }
    }, [currentCall?.id]),

    onCallEnded: useCallback((data) => {
      console.log("🔚 Call ended:", data);
      
      // Remove from active calls
      setActiveCalls(prev => prev.filter(call => call.id !== data.callId));
      
      // If it's the current call, reset state
      if (currentCall?.id === data.callId) {
        setCurrentCall(null);
        setIsInCall(false);
        setCallParticipants([]);
      }

      callbacksRef.current.onCallEnded?.(data.callId);
    }, [currentCall?.id]),

    onConnectionError: useCallback((error) => {
      console.error("❌ Call notification connection error:", error);
      callbacksRef.current.onError?.("Failed to connect to call notifications");
    }, [])
  });

  // Load active calls for chat
  const loadActiveCallsForChat = useCallback(async (targetChatId: string) => {
    try {
      setIsLoading(true);
      const calls = await CallManagementService.getActiveCallsForChat(targetChatId);
      setActiveCalls(calls);
      return calls;
    } catch (error: any) {
      console.error("❌ Failed to load active calls:", error);
      callbacksRef.current.onError?.(error.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh call participants
  const refreshCallParticipants = useCallback(async (callId: string) => {
    try {
      const participants = await CallManagementService.getCallParticipants(callId);
      setCallParticipants(participants);
      return participants;
    } catch (error: any) {
      console.error("❌ Failed to refresh call participants:", error);
      callbacksRef.current.onError?.(error.message);
      return [];
    }
  }, []);

  // Initiate a new call
  const initiateCall = useCallback(async (
    callType: "audio" | "video",
    participantIds: string[]
  ): Promise<ActiveCall | null> => {
    if (!userId || !chatId) {
      callbacksRef.current.onError?.("User ID and Chat ID are required to initiate a call");
      return null;
    }

    try {
      setIsLoading(true);
      
      const channelName = CallManagementService.generateChannelName(chatId);
      
      const request: InitiateCallRequest = {
        chatId,
        initiatedBy: userId,
        callType,
        channelName,
        participantIds: [userId, ...participantIds] // Include self
      };

      const call = await CallManagementService.initiateCall(request);
      
      setCurrentCall(call);
      setActiveCalls(prev => [...prev, call]);
      
      callbacksRef.current.onCallInitiated?.(call);
      
      return call;
    } catch (error: any) {
      console.error("❌ Failed to initiate call:", error);
      callbacksRef.current.onError?.(error.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [userId, chatId]);

  // Join an existing call
  const joinCall = useCallback(async (call: ActiveCall): Promise<boolean> => {
    if (!userId) {
      callbacksRef.current.onError?.("User ID is required to join a call");
      return false;
    }

    try {
      setIsLoading(true);
      
      const agoraUid = CallManagementService.generateAgoraUid();
      
      const request: JoinCallRequest = {
        userId,
        agoraUid
      };

      await CallManagementService.joinCall(call.id, request);
      
      setCurrentCall(call);
      setIsInCall(true);
      
      // Refresh participants
      await refreshCallParticipants(call.id);
      
      callbacksRef.current.onCallJoined?.(call);
      
      return true;
    } catch (error: any) {
      console.error("❌ Failed to join call:", error);
      callbacksRef.current.onError?.(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userId, refreshCallParticipants]);

  // Leave current call
  const leaveCall = useCallback(async (reason?: string): Promise<boolean> => {
    if (!currentCall || !userId) {
      return false;
    }

    try {
      setIsLoading(true);
      
      await CallManagementService.leaveCall(currentCall.id, {
        userId,
        reason
      });
      
      setIsInCall(false);
      
      // Don't reset currentCall immediately - let the notification handle it
      callbacksRef.current.onCallLeft?.(currentCall.id);
      
      return true;
    } catch (error: any) {
      console.error("❌ Failed to leave call:", error);
      callbacksRef.current.onError?.(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentCall, userId]);

  // End current call (only initiator can do this)
  const endCall = useCallback(async (reason?: string): Promise<boolean> => {
    if (!currentCall || !userId) {
      return false;
    }

    try {
      setIsLoading(true);
      
      await CallManagementService.endCall(currentCall.id, {
        userId,
        reason
      });
      
      // Track video call activity for professional monitoring
      try {
        const participants = callParticipants || currentCall?.participants || [];
        
        // Find professional with multiple fallback checks
        const professionalParticipant = participants.find((p: any) => {
          const roleId = Number(p.role?.id || p.roleId || p.role || p.user?.role?.id);
          return roleId === 4 || roleId === 5 || roleId === 6;
        });
        
        if (professionalParticipant) {
          const professionalId = professionalParticipant.userId || professionalParticipant.id;
          
          if (professionalId) {
            const callStartTime = currentCall.startedAt ? new Date(currentCall.startedAt).getTime() : Date.now();
            const callEndTime = Date.now();
            const callDuration = Math.max(0, Math.floor((callEndTime - callStartTime) / 1000));
            
            const trackingData = {
              professionalId: professionalId.toString(),
              activityType: "video_call",
              metadata: {
                professionalType: "remote",
                callDuration: callDuration,
                callId: currentCall.id,
                callType: currentCall.callType
              }
            };

            await store.dispatch(trackActivity(trackingData) as any);
          }
        }
      } catch (trackError: any) {
        // Silent error handling
      }
      
      setCurrentCall(null);
      setIsInCall(false);
      setCallParticipants([]);
      
      callbacksRef.current.onCallEnded?.(currentCall.id);
      
      return true;
    } catch (error: any) {
      console.error("❌ Failed to end call:", error);
      callbacksRef.current.onError?.(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentCall, userId, callParticipants]);

  // Toggle audio for current user
  const toggleAudio = useCallback(async (isEnabled: boolean): Promise<boolean> => {
    if (!currentCall || !userId) {
      return false;
    }

    try {
      await CallManagementService.toggleParticipantAudio(currentCall.id, userId, { isEnabled });
      
      // Update local state
      setCallParticipants(prev => 
        prev.map(participant => 
          participant.userId === userId 
            ? { ...participant, isAudioEnabled: isEnabled }
            : participant
        )
      );
      
      return true;
    } catch (error: any) {
      console.error("❌ Failed to toggle audio:", error);
      callbacksRef.current.onError?.(error.message);
      return false;
    }
  }, [currentCall, userId]);

  // Toggle video for current user
  const toggleVideo = useCallback(async (isEnabled: boolean): Promise<boolean> => {
    if (!currentCall || !userId) {
      return false;
    }

    try {
      await CallManagementService.toggleParticipantVideo(currentCall.id, userId, { isEnabled });
      
      // Update local state
      setCallParticipants(prev => 
        prev.map(participant => 
          participant.userId === userId 
            ? { ...participant, isVideoEnabled: isEnabled }
            : participant
        )
      );
      
      return true;
    } catch (error: any) {
      console.error("❌ Failed to toggle video:", error);
      callbacksRef.current.onError?.(error.message);
      return false;
    }
  }, [currentCall, userId]);

  // Load active calls when chatId changes
  useEffect(() => {
    if (chatId) {
      loadActiveCallsForChat(chatId);
    }
  }, [chatId, loadActiveCallsForChat]);

  // Get current user's participation status
  const currentUserParticipant = currentCall 
    ? callParticipants.find(p => p.userId === userId)
    : null;

  const isCurrentUserInCall = currentUserParticipant?.status === "connected";

  return {
    // State
    activeCalls,
    currentCall,
    isInCall,
    isLoading,
    callParticipants,
    currentUserParticipant,
    isCurrentUserInCall,
    isConnected,

    // Actions
    initiateCall,
    joinCall,
    leaveCall,
    endCall,
    toggleAudio,
    toggleVideo,
    loadActiveCallsForChat,
    refreshCallParticipants,

    // Utilities
    canEndCall: currentCall?.initiatedBy === userId,
    activeCallsCount: activeCalls.length,
    hasActiveCallInChat: activeCalls.length > 0
  };
};
